import React from 'react'
import { UpsellDialogs } from 'src/components/apps-integrations/UpsellDialogs'
import UpsellUpgradeNudge from 'src/components/apps-integrations/UpsellUpgradeNudge'
import type { AppExtensionDefinition, AppExtensionUi } from './types'
import {
  DowngradeRetryVia,
  UpsellDialogKey,
  type UpsellDialogsState,
  UpsellEntryPoint,
  type UpsellPlanId,
  type UpsellTableData
} from 'src/lib/types/appsIntegrations'
import { getErrorCode } from 'src/lib/utils/helpers'
import { notifyUpsellTierUpdated, parseUpsellDegradeErrorData } from 'src/lib/utils/upsellHelpers'
import { TOO_MANY_ACTIVE_UPSELLS_ERROR_CODE } from 'src/lib/utils/constants'

// This is the main file controlling the upsell modals and functionality over the installation and uninstallation flow - for the upcoming apps as well, we will stick to this similar approach.

const isUpsellPlanId = (plan?: string | null): plan is UpsellPlanId => plan === 'lite' || plan === 'plus'

/** Upsell's typed view of the shared AppExtensionUi state. */
type UpsellUiState = {
  /** The currently open dialog — opened through ui.openDialog from useAppExtensionState. */
  activeDialog: UpsellDialogKey | null
  /** The plan picked in handleSelectPlan, set via ui.patchPayload while its request is in flight. */
  selectingPlanId: UpsellPlanId | null
  /** The upsells from the FSE_001 downgrade error. */
  degradeUpsells: UpsellTableData[]
  /** Kept apart from selectingPlanId: that one blocks closing, and the degrade dialog's Back must still work. */
  downgradeTargetPlanId: UpsellPlanId | null
  downgradeRetryVia: DowngradeRetryVia
  /** The dialog the downgrade picker was opened from — its Back arrow returns there. */
  returnToDialog: UpsellDialogKey | null
  /** Where the plan picker was opened from (for its activity log) — kept through the downgrade picker so back reopens it with the same one. */
  planModalEntryPoint: UpsellEntryPoint
  isPlanModalReopened: boolean
}

// ui.dialog/ui.payload are generic (shared shape across all extensions) — cast them to Upsell's own keys, with defaults, in one place.
const mapUpsellUiState = ({ dialog, payload }: AppExtensionUi): UpsellUiState => ({
  activeDialog: dialog as UpsellDialogKey | null,
  selectingPlanId: (payload.selectingPlanId as UpsellPlanId | undefined) ?? null,
  degradeUpsells: (payload.upsells as UpsellTableData[] | undefined) ?? [],
  downgradeTargetPlanId: (payload.downgradeTargetPlanId as UpsellPlanId | undefined) ?? null,
  downgradeRetryVia: (payload.downgradeRetryVia as DowngradeRetryVia | undefined) ?? DowngradeRetryVia.TIER_UPDATE,
  returnToDialog: (payload.returnToDialog as UpsellDialogKey | undefined) ?? null,
  planModalEntryPoint: (payload.planModalEntryPoint as UpsellEntryPoint | undefined) ?? UpsellEntryPoint.APP_STORE,
  isPlanModalReopened: Boolean(payload.isPlanModalReopened)
})

export const upsellExtension: AppExtensionDefinition = {
  key: 'upsell',
  matches: ({ appKey, redirectUrl, source }) => source === 'app' && (appKey === 'UPSELL' || redirectUrl === '/upsell'),
  build: (ctx, ui) => {
    const currentPlan = isUpsellPlanId(ctx.currentPlan) ? ctx.currentPlan : null

    const {
      activeDialog,
      selectingPlanId,
      degradeUpsells,
      downgradeTargetPlanId,
      downgradeRetryVia,
      returnToDialog,
      planModalEntryPoint,
      isPlanModalReopened
    } = mapUpsellUiState(ui)

    const openPlansDialog = (entryPoint: UpsellEntryPoint) =>
      ui.openDialog(UpsellDialogKey.plans, { planModalEntryPoint: entryPoint })

    const openCancellationWarningDialog = () => ui.openDialog(UpsellDialogKey.cancellationWarning)

    const closeDialog = () => {
      if (ctx.isLoading || selectingPlanId) return
      ui.closeDialog()
    }

    // FSE_001: the chosen tier can't hold every active upsell — open the downgrade picker with the upsells from the error `retryVia` remembers which call failed, so "Continue" retries that same call with the ids to keep.
    const openDowngradePickerIfTooManyUpsells = (
      planId: UpsellPlanId,
      error: unknown,
      retryVia: DowngradeRetryVia
    ): boolean => {
      if (getErrorCode(error) !== TOO_MANY_ACTIVE_UPSELLS_ERROR_CODE) return false
      const { upsells } = parseUpsellDegradeErrorData(error)
      ui.openDialog(UpsellDialogKey.degradePlan, {
        upsells,
        downgradeTargetPlanId: planId,
        downgradeRetryVia: retryVia,
        returnToDialog: activeDialog,
        planModalEntryPoint
      })
      return true
    }

    const handleSelectPlan = (planId: UpsellPlanId, selectedUpsellIds?: string[]) => {
      // Retry from the downgrade picker: a failed install retries activation, a failed tier change retries /tier — both with the upsells to keep.
      if (selectedUpsellIds) {
        const data = { active_upsell_ids: selectedUpsellIds }
        if (downgradeRetryVia === DowngradeRetryVia.ACTIVATE) {
          ctx.onActivate(planId, { target_tier_id: planId, data }).then(result => {
            if (!result.success) return
            ui.closeDialog()
          })
          return
        }
        ctx
          .onTierUpdate(planId, {
            current_tier_id: currentPlan ?? 'plus',
            target_tier_id: planId,
            data
          })
          .then(result => {
            if (!result.success) return
            ui.closeDialog()
            notifyUpsellTierUpdated(planId)
          })
        return
      }

      // Installation — no plan yet, so activate straight on the chosen tier. Installing Lite over more active upsells than Lite allows fails with FSE_001, which goes through the same downgrade picker as a Plus → Lite move.
      if (!currentPlan) {
        ui.patchPayload({ selectingPlanId: planId })
        ctx.onActivate(planId).then(result => {
          if (result.success) {
            ui.closeDialog()
            return
          }
          if (openDowngradePickerIfTooManyUpsells(planId, result.error, DowngradeRetryVia.ACTIVATE)) return
          ui.patchPayload({ selectingPlanId: null })
        })
        return
      }

      if (planId === currentPlan) return

      // Tier change. Upgrade (lite → plus) never needs extra data. Downgrade (plus → lite) is first tried without data too FSE_001 opens the downgrade picker, and its selection is sent on the retry above.
      ui.patchPayload({ selectingPlanId: planId })
      ctx.onTierUpdate(planId, { current_tier_id: currentPlan, target_tier_id: planId }).then(result => {
        if (result.success) {
          ui.closeDialog()
          notifyUpsellTierUpdated(planId)
          return
        }
        if (openDowngradePickerIfTooManyUpsells(planId, result.error, DowngradeRetryVia.TIER_UPDATE)) return
        ui.patchPayload({ selectingPlanId: null })
      })
    }

    const handleContinueCancellation = () => {
      if (ctx.isLoading) return
      ctx.onProceedToUninstall()
      ui.closeDialog()
    }

    const handleSwitchToLite = () => {
      if (ctx.isLoading) return
      openPlansDialog(UpsellEntryPoint.DOWNGRADE)
    }

    // Reopens the previous dialog with a fresh payload, so the plan picker doesn't come back stuck on selectingPlanId's spinner.
    const handleDowngradeBack = () => {
      if (ctx.isLoading) return
      if (returnToDialog) ui.openDialog(returnToDialog, { planModalEntryPoint, isPlanModalReopened: true })
      else ui.closeDialog()
    }

    const dialogsState: UpsellDialogsState = {
      currentPlan,
      isLoading: ctx.isLoading,
      activeDialog,
      planModalEntryPoint,
      isPlanModalReopened,
      selectingPlanId,
      closeDialog,
      handleSelectPlan,
      handleContinueCancellation,
      handleSwitchToLite,
      handleDowngradeBack
    }

    return {
      menuActions: [
        { key: 'view-plans', label: 'View Plans', onClick: () => openPlansDialog(UpsellEntryPoint.APP_STORE) }
      ],
      cta: {
        label: ctx.isActive ? undefined : 'Install',
        onClick: () => {
          if (!ctx.isActive) openPlansDialog(UpsellEntryPoint.APP_STORE)
          else if (currentPlan === 'plus') openCancellationWarningDialog()
          else ctx.onProceedToUninstall()
        }
      },
      sidebarSlot:
        currentPlan === 'lite' ? (
          <UpsellUpgradeNudge
            buttonConfig={{ onUpgradeClick: () => openPlansDialog(UpsellEntryPoint.BANNER) }}
            upgradePlan={currentPlan}
          />
        ) : null,

      // Only Plus gets the "you'll lose these features" warning first - Lite goes straight to the cancellation reason.
      interceptUninstall: currentPlan === 'plus' ? openCancellationWarningDialog : undefined,
      renderDialogs: () => (
        <UpsellDialogs
          state={dialogsState}
          upsells={degradeUpsells}
          activeUpsellsCount={degradeUpsells.filter(u => u.is_active).length}
          onDegradeContinue={(selectedUpsellIds: string[]) => {
            if (downgradeTargetPlanId) handleSelectPlan(downgradeTargetPlanId, selectedUpsellIds)
          }}
        />
      )
    }
  }
}
